import { supabase } from '../supabase';
import { DealService, Deal, Service } from '../types';

export interface ContractRenewalData {
  renewal_reminder_date?: string;
  contract_expiry_date?: string;
  renewal_status?: 'active' | 'expired' | 'renewed' | 'cancelled';
  renewal_notice_days?: number;
  auto_renewal_enabled?: boolean;
  requires_manual_renewal?: boolean;
}

export interface RenewalTask {
  id: string;
  deal_id: string;
  deal_service_id: string;
  service_name: string;
  client_name: string;
  expiry_date: string;
  days_until_expiry: number;
  assigned_to: string;
  priority: 'low' | 'medium' | 'high' | 'urgent';
  status: 'pending' | 'completed';
}

class ContractRenewalService {
  /**
   * Inizializza il sistema di rinnovo per un deal service
   */
  async initializeContractRenewal(
    dealServiceId: string, 
    expiryDate: string, 
    reminderDays: number = 30
  ): Promise<boolean> {
    try {
      const { error } = await supabase
        .from('deal_services')
        .update({
          contract_expiry_date: expiryDate,
          renewal_notice_days: reminderDays,
          renewal_reminder_date: this.calculateReminderDate(expiryDate, reminderDays),
          renewal_status: 'active',
          requires_manual_renewal: true
        })
        .eq('id', dealServiceId);

      if (error) throw error;
      return true;
    } catch (error) {
      console.error('Error initializing contract renewal:', error);
      return false;
    }
  }

  /**
   * Crea attività automatiche per rinnovi in scadenza
   */
  async createRenewalTasks(): Promise<number> {
    try {
      // Trova servizi in scadenza nei prossimi giorni che richiedono rinnovo manuale
      const { data: expiringServices, error: fetchError } = await supabase
        .from('deal_services')
        .select(`
          id,
          contract_expiry_date,
          renewal_notice_days,
          renewal_status,
          requires_manual_renewal,
          renewal_task_created,
          deals!inner(id, title, assigned_to, client_id, clients(business_name)),
          services!inner(id, title, service_type)
        `)
        .eq('requires_manual_renewal', true)
        .eq('renewal_status', 'active')
        .eq('renewal_task_created', false)
        .lte('renewal_reminder_date', new Date().toISOString().split('T')[0]);

      if (fetchError) throw fetchError;

      let activitiesCreated = 0;

      for (const service of expiringServices || []) {
        const daysUntilExpiry = this.calculateDaysUntilExpiry(service.contract_expiry_date);
        
        // Crea attività di rinnovo
        const activityData = {
          title: `Rinnovo contratto: ${service.services.title}`,
          description: `Il contratto per il servizio "${service.services.title}" del cliente "${service.deals.clients.business_name}" scadrà il ${new Date(service.contract_expiry_date).toLocaleDateString('it-IT')}.\n\n[RENEWAL_TASK:${service.id}]`,
          type: 'task',
          status: 'pending',
          assigned_to: service.deals.assigned_to,
          user_id: service.deals.assigned_to,
          deal_id: service.deals.id,
          due_date: service.contract_expiry_date,
          organization_id: await this.getCurrentOrganizationId()
        };

        // Crea attività nella tabella sales_activities
        const { error: createError } = await supabase
          .from('sales_activities')
          .insert(activityData);

        if (createError) {
          console.error('Error creating renewal activity:', createError);
          continue;
        }

        // Marca il servizio come "attività creata"
        await supabase
          .from('deal_services')
          .update({ renewal_task_created: true })
          .eq('id', service.id);

        activitiesCreated++;

        // Crea notifica per l'utente assegnato
        await this.createRenewalNotification(service, daysUntilExpiry);
      }

      return activitiesCreated;
    } catch (error) {
      console.error('Error creating renewal activities:', error);
      return 0;
    }
  }

  /**
   * Gestisce il rinnovo di un contratto
   */
  async processContractRenewal(
    dealServiceId: string,
    renewalData: {
      newExpiryDate?: string;
      renewalStatus: 'renewed' | 'cancelled';
      notes?: string;
      dealId?: string;
    }
  ): Promise<boolean> {
    try {
      // Get current service data before update
      const { data: serviceData } = await supabase
        .from('deal_services')
        .select(`
          id,
          renewal_status,
          recurring_status,
          deal_id,
          deals!inner (
            id,
            title
          ),
          services (
            title
          )
        `)
        .eq('id', dealServiceId)
        .maybeSingle();

      const dealId = renewalData.dealId || serviceData?.deal_id;

      const updateData: any = {
        renewal_status: renewalData.renewalStatus
      };

      if (renewalData.newExpiryDate) {
        updateData.contract_expiry_date = renewalData.newExpiryDate;
        updateData.renewal_reminder_date = this.calculateReminderDate(
          renewalData.newExpiryDate,
          30 // Default 30 giorni di preavviso
        );
        updateData.renewal_task_created = false; // Reset per creare nuova task
      }

      const { error } = await supabase
        .from('deal_services')
        .update(updateData)
        .eq('id', dealServiceId);

      if (error) throw error;

      // Chiudi attività di rinnovo correlate usando il tag nella descrizione
      const { data: closedByTag } = await supabase
        .from('sales_activities')
        .update({
          status: 'completed',
          completed_at: new Date().toISOString()
        })
        .like('description', `%[RENEWAL_TASK:${dealServiceId}]%`)
        .eq('type', 'task')
        .neq('status', 'completed')
        .select('id');

      // Fallback: se il tag non ha matchato nessuna task (possibile mismatch di id),
      // chiudi le task di rinnovo per deal_id
      if ((!closedByTag || closedByTag.length === 0) && dealId) {
        await supabase
          .from('sales_activities')
          .update({
            status: 'completed',
            completed_at: new Date().toISOString()
          })
          .eq('deal_id', dealId)
          .eq('type', 'task')
          .neq('status', 'completed')
          .like('description', '%[RENEWAL_TASK:%');
      }

      return true;
    } catch (error) {
      console.error('Error processing contract renewal:', error);
      return false;
    }
  }

  /**
   * Ottiene servizi in scadenza
   */
  async getExpiringContracts(daysAhead: number = 30): Promise<RenewalTask[]> {
    try {
      const targetDate = new Date();
      targetDate.setDate(targetDate.getDate() + daysAhead);

      const { data, error } = await supabase
        .from('deal_services')
        .select(`
          id,
          contract_expiry_date,
          renewal_status,
          deals!inner(id, title, assigned_to, clients(business_name)),
          services!inner(title)
        `)
        .eq('requires_manual_renewal', true)
        .eq('renewal_status', 'active')
        .lte('contract_expiry_date', targetDate.toISOString().split('T')[0])
        .order('contract_expiry_date', { ascending: true });

      if (error) throw error;

      return (data || []).map(service => ({
        id: service.id,
        deal_id: service.deals.id,
        deal_service_id: service.id,
        service_name: service.services.title,
        client_name: service.deals.clients.business_name,
        expiry_date: service.contract_expiry_date,
        days_until_expiry: this.calculateDaysUntilExpiry(service.contract_expiry_date),
        assigned_to: service.deals.assigned_to,
        priority: this.calculateTaskPriority(this.calculateDaysUntilExpiry(service.contract_expiry_date)),
        status: 'pending' // Le attività verranno caricate separatamente se necessario
      }));
    } catch (error) {
      console.error('Error fetching expiring contracts:', error);
      return [];
    }
  }

  /**
   * Aggiorna lo status di un rinnovo
   */
  async updateRenewalStatus(dealServiceId: string, status: 'active' | 'expired' | 'renewed' | 'cancelled'): Promise<boolean> {
    try {
      const { error } = await supabase
        .from('deal_services')
        .update({ 
          renewal_status: status
        })
        .eq('id', dealServiceId);

      if (error) throw error;
      return true;
    } catch (error) {
      console.error('Error updating renewal status:', error);
      return false;
    }
  }

  /**
   * Inizializza rinnovi per tutti i servizi di un deal quando viene firmato
   */
  async initializeRenewalsForDeal(dealId: string): Promise<void> {
    try {
      console.log(`Initializing renewals for deal: ${dealId}`);
      
      const { data: dealServices, error } = await supabase
        .from('deal_services')
        .select(`
          id,
          contract_expiry_date,
          renewal_status,
          renewal_task_created,
          requires_manual_renewal,
          services(id, title, service_type, has_renewal_tasks, renewal_task_intervals),
          payment_schedule
        `)
        .eq('deal_id', dealId);

      if (error) throw error;

      console.log(`Found ${dealServices?.length || 0} deal services to process`);

      for (const dealService of dealServices || []) {
        // Skip if already initialized (standard renewals OR custom renewal tasks)
        if ((dealService.contract_expiry_date && dealService.renewal_status) || dealService.renewal_task_created) {
          console.log(`Service ${dealService.id} already has renewal data, skipping`);
          continue;
        }

        const service = dealService.services;
        if (!service) {
          console.log(`Service data not found for deal service ${dealService.id}, skipping`);
          continue;
        }
        
        console.log(`Processing service: ${service.title} (type: ${service.service_type})`);
        
        // Verifica se il servizio ha task di rinnovo personalizzate
        if (service.has_renewal_tasks && service.renewal_task_intervals && service.renewal_task_intervals.length > 0) {
          console.log(`🔄 Service ${service.title} has custom renewal tasks configured: ${service.renewal_task_intervals.length} intervals`);
          
          // Crea sales activities personalizzate basate sui renewal_task_intervals
          await this.createCustomRenewalTasks(dealId, dealService.id, service);
        } else {
          // Fallback alla logica esistente per servizi ricorrenti senza configurazioni personalizzate
          if (service.service_type === 'monthly' || service.service_type === 'annual') {
            const expiryDate = this.calculateServiceExpiryDate(
              dealService.payment_schedule,
              service.service_type
            );

            const reminderDate = this.calculateReminderDate(expiryDate, 30);

            const updateData = {
              contract_expiry_date: expiryDate,
              renewal_status: 'active' as const,
              renewal_notice_days: 30,
              renewal_reminder_date: reminderDate,
              next_renewal_date: expiryDate,
              renewal_task_created: false,
              // Servizi mensili si rinnovano automaticamente, annuali richiedono rinnovo manuale
              requires_manual_renewal: service.service_type === 'annual'
            };

            console.log(`📊 Service details: service_type=${service.service_type}, requires_manual_renewal=${updateData.requires_manual_renewal}`);
            console.log(`📅 Dates: expiry=${expiryDate}, reminder=${reminderDate}`);

            const { error: updateError } = await supabase
              .from('deal_services')
              .update(updateData)
              .eq('id', dealService.id);

            if (updateError) {
              console.error(`Error updating deal service ${dealService.id}:`, updateError);
            } else {
              console.log(`✅ Initialized renewal for ${service.title}: expiry=${expiryDate}, manual=${updateData.requires_manual_renewal}`);
            }
          } else {
            console.log(`Service ${service.title} is ${service.service_type}, not setting up renewal`);
          }
        }
      }
      
      console.log(`✅ Completed renewal initialization for deal ${dealId}`);
    } catch (error) {
      console.error('Error initializing renewals for deal:', error);
    }
  }

  /**
   * Ricalcola le date delle task di rinnovo quando cambia la data di accettazione del deal
   */
  async updateRenewalTaskDates(dealId: string, newSignedAt: string): Promise<number> {
    try {
      console.log(`Recalculating renewal task dates for deal ${dealId} with new signed_at: ${newSignedAt}`);

      // Fetch all pending renewal tasks for this deal
      const { data: renewalTasks, error } = await supabase
        .from('sales_activities')
        .select('id, description, due_date, metadata')
        .eq('deal_id', dealId)
        .eq('type', 'task')
        .neq('status', 'completed')
        .like('description', '%[RENEWAL_TASK:%');

      if (error) throw error;
      if (!renewalTasks || renewalTasks.length === 0) return 0;

      const newBaseDate = new Date(newSignedAt);
      let updatedCount = 0;

      for (const task of renewalTasks) {
        const metadata = task.metadata as any;
        if (!metadata?.is_custom_renewal_task) continue;

        // Calculate new due date from interval
        const newDueDate = new Date(newBaseDate);
        if (metadata.renewal_interval_months !== undefined) {
          newDueDate.setMonth(newDueDate.getMonth() + metadata.renewal_interval_months);
        } else if (metadata.renewal_interval_days !== undefined) {
          newDueDate.setDate(newDueDate.getDate() + metadata.renewal_interval_days);
        } else {
          continue; // No interval info, skip
        }

        // Update description's "Scadenza:" line
        let updatedDescription = task.description;
        if (updatedDescription) {
          updatedDescription = updatedDescription.replace(
            /Scadenza: [^\n]+/,
            `Scadenza: ${newDueDate.toLocaleDateString('it-IT')}`
          );
        }

        // Update task
        const { error: updateError } = await supabase
          .from('sales_activities')
          .update({
            due_date: newDueDate.toISOString(),
            description: updatedDescription,
            metadata: {
              ...metadata,
              base_date: newBaseDate.toISOString()
            }
          })
          .eq('id', task.id);

        if (updateError) {
          console.error(`Error updating renewal task ${task.id}:`, updateError);
        } else {
          updatedCount++;
        }
      }

      console.log(`Updated ${updatedCount} renewal task dates for deal ${dealId}`);
      return updatedCount;
    } catch (error) {
      console.error('Error updating renewal task dates:', error);
      return 0;
    }
  }

  /**
   * Ritorna, per ogni deal_service con rinnovo personalizzato, la scadenza (due_date) e il
   * titolo della task di rinnovo aperta più corretta — la stessa fonte già usata da Dashboard
   * e Cronologia Note (sales_activities con metadata.is_custom_renewal_task), così che
   * "Rinnovi Contratti" non dipenda più da deal_services.contract_expiry_date, che non viene
   * mai ricalcolato quando cambia signed_at.
   */
  async getCustomRenewalDueDates(dealId: string): Promise<Map<string, { dueDate: string; title: string }>> {
    const result = new Map<string, { dueDate: string; title: string }>();
    try {
      const { data: renewalTasks, error } = await supabase
        .from('sales_activities')
        .select('title, due_date, metadata, created_at')
        .eq('deal_id', dealId)
        .eq('type', 'task')
        .neq('status', 'completed')
        .like('description', '%[RENEWAL_TASK:%')
        .order('due_date', { ascending: true });

      if (error) throw error;

      for (const task of renewalTasks || []) {
        const metadata = task.metadata as any;
        if (!metadata?.is_custom_renewal_task || !metadata?.deal_service_id || !task.due_date) continue;

        // Se un deal_service ha più task aperte, tiene la più vicina (già ordinate per due_date asc)
        if (!result.has(metadata.deal_service_id)) {
          result.set(metadata.deal_service_id, { dueDate: task.due_date, title: task.title });
        }
      }
    } catch (error) {
      console.error('Error fetching custom renewal due dates:', error);
    }
    return result;
  }

  /**
   * Ricalcola le date delle task istanze (da template) quando cambia la data di accettazione del deal
   */
  async updateServiceTaskInstanceDates(dealId: string, newSignedAt: string): Promise<number> {
    try {
      console.log(`Recalculating service task instance dates for deal ${dealId}`);

      // Fetch pending task instances with their template's trigger_days_offset
      const { data: instances, error } = await supabase
        .from('service_task_instances')
        .select('id, template_id, due_date, template:service_task_templates(trigger_days_offset)')
        .eq('deal_id', dealId)
        .neq('status', 'completed');

      if (error) throw error;
      if (!instances || instances.length === 0) return 0;

      const newBaseDate = new Date(newSignedAt);
      let updatedCount = 0;

      for (const instance of instances) {
        const template = instance.template as any;
        const offset = template?.trigger_days_offset;
        if (offset === undefined || offset === null) continue;

        const newDueDate = new Date(newBaseDate);
        newDueDate.setDate(newDueDate.getDate() + offset);

        const { error: updateError } = await supabase
          .from('service_task_instances')
          .update({ due_date: newDueDate.toISOString().split('T')[0] })
          .eq('id', instance.id);

        if (updateError) {
          console.error(`Error updating task instance ${instance.id}:`, updateError);
        } else {
          updatedCount++;
        }
      }

      console.log(`Updated ${updatedCount} service task instance dates for deal ${dealId}`);
      return updatedCount;
    } catch (error) {
      console.error('Error updating service task instance dates:', error);
      return 0;
    }
  }

  /**
   * Ricalcola le due_date delle task generate da template (sia project tasks che deal tasks)
   * quando cambia la data di accettazione (signed_at) del deal.
   * Identifica le task tramite metadata.source = 'service_task_template'.
   */
  async recalculateTemplateTaskDates(dealId: string, newSignedAt: string): Promise<number> {
    try {
      console.log(`Recalculating template task dates for deal ${dealId} with new signed_at ${newSignedAt}`);
      const newBaseDate = new Date(newSignedAt);
      let updatedCount = 0;

      // 1. Deal tasks (sales_activities con metadata template)
      const { data: dealTasks, error: dealTasksError } = await supabase
        .from('sales_activities')
        .select('id, metadata')
        .eq('deal_id', dealId)
        .eq('type', 'task')
        .neq('status', 'completed');

      if (dealTasksError) {
        console.error('Error fetching deal tasks for recalculation:', dealTasksError);
      } else {
        for (const task of (dealTasks || [])) {
          const meta = task.metadata as any;
          if (meta?.source !== 'service_task_template' || meta?.trigger_days_offset == null) continue;

          const offset = Number(meta.trigger_days_offset);
          if (isNaN(offset)) continue;

          const newDueDate = new Date(newBaseDate.getTime() + offset * 24 * 60 * 60 * 1000);
          const { error } = await supabase
            .from('sales_activities')
            .update({ due_date: newDueDate.toISOString() })
            .eq('id', task.id);
          if (!error) updatedCount++;
        }
      }

      // 2. Project tasks (tasks via projects.deal_id)
      const { data: projects, error: projError } = await supabase
        .from('projects')
        .select('id')
        .eq('deal_id', dealId);

      if (projError) {
        console.error('Error fetching projects for task recalculation:', projError);
      } else if (projects && projects.length > 0) {
        const projectIds = projects.map(p => p.id);
        const { data: projectTasks, error: ptError } = await supabase
          .from('tasks')
          .select('id, metadata')
          .in('project_id', projectIds)
          .neq('status', 'completed');

        if (ptError) {
          console.error('Error fetching project tasks for recalculation:', ptError);
        } else {
          for (const task of (projectTasks || [])) {
            const meta = task.metadata as any;
            if (meta?.source !== 'service_task_template' || meta?.trigger_days_offset == null) continue;

            const offset = Number(meta.trigger_days_offset);
            if (isNaN(offset)) continue;

            const newDueDate = new Date(newBaseDate.getTime() + offset * 24 * 60 * 60 * 1000);
            const dueDateStr = newDueDate.toISOString().split('T')[0];
            const { error } = await supabase
              .from('tasks')
              .update({ due_date: dueDateStr })
              .eq('id', task.id);
            if (!error) updatedCount++;
          }
        }
      }

      console.log(`Recalculated ${updatedCount} template-generated task dates for deal ${dealId}`);
      return updatedCount;
    } catch (error) {
      console.error('Error recalculating template task dates:', error);
      return 0;
    }
  }

  /**
   * Shifta le due_date delle task manuali (NON generate da template) quando cambia signed_at.
   * Le task da template sono già gestite da recalculateTemplateTaskDates().
   */
  async shiftManualTaskDates(dealId: string, oldSignedAt: string, newSignedAt: string): Promise<number> {
    try {
      const deltaMs = new Date(newSignedAt).getTime() - new Date(oldSignedAt).getTime();
      if (deltaMs === 0) return 0;

      console.log(`Shifting manual task dates for deal ${dealId} by ${deltaMs / (24 * 60 * 60 * 1000)} days`);
      let updatedCount = 0;

      // 1. Deal tasks (sales_activities senza metadata template)
      const { data: dealTasks, error: dealTasksError } = await supabase
        .from('sales_activities')
        .select('id, due_date, metadata')
        .eq('deal_id', dealId)
        .eq('type', 'task')
        .neq('status', 'completed')
        .not('due_date', 'is', null);

      if (dealTasksError) {
        console.error('Error fetching deal tasks for manual shift:', dealTasksError);
      } else {
        for (const task of (dealTasks || [])) {
          const meta = task.metadata as any;
          if (meta?.source === 'service_task_template') continue;
          if (meta?.is_custom_renewal_task) continue;

          const newDueDate = new Date(new Date(task.due_date).getTime() + deltaMs);
          const { error } = await supabase
            .from('sales_activities')
            .update({ due_date: newDueDate.toISOString() })
            .eq('id', task.id);
          if (!error) updatedCount++;
        }
      }

      // 2. Project tasks (tasks via projects.deal_id)
      const { data: projects, error: projError } = await supabase
        .from('projects')
        .select('id')
        .eq('deal_id', dealId);

      if (projError) {
        console.error('Error fetching projects for manual task shift:', projError);
      } else if (projects && projects.length > 0) {
        const projectIds = projects.map(p => p.id);
        const { data: projectTasks, error: ptError } = await supabase
          .from('tasks')
          .select('id, due_date, metadata')
          .in('project_id', projectIds)
          .neq('status', 'completed')
          .not('due_date', 'is', null);

        if (ptError) {
          console.error('Error fetching project tasks for manual shift:', ptError);
        } else {
          for (const task of (projectTasks || [])) {
            const meta = task.metadata as any;
            if (meta?.source === 'service_task_template') continue;

            const newDueDate = new Date(new Date(task.due_date).getTime() + deltaMs);
            const dueDateStr = newDueDate.toISOString().split('T')[0];
            const { error } = await supabase
              .from('tasks')
              .update({ due_date: dueDateStr })
              .eq('id', task.id);
            if (!error) updatedCount++;
          }
        }
      }

      console.log(`Shifted ${updatedCount} manual task dates for deal ${dealId}`);
      return updatedCount;
    } catch (error) {
      console.error('Error shifting manual task dates:', error);
      return 0;
    }
  }

  /**
   * Crea sales activities personalizzate basate sui renewal_task_intervals del servizio
   */
  private async createCustomRenewalTasks(dealId: string, dealServiceId: string, service: any): Promise<void> {
    try {
      // Check difensivo: verifica che non esistano già task di rinnovo per questo deal_service
      const { data: existingTasks } = await supabase
        .from('sales_activities')
        .select('id')
        .like('description', `%[RENEWAL_TASK:${dealServiceId}]%`)
        .eq('type', 'task')
        .limit(1);

      if (existingTasks && existingTasks.length > 0) {
        console.log(`Custom renewal tasks already exist for deal_service ${dealServiceId}, skipping`);
        return;
      }

      const organizationId = await this.getCurrentOrganizationId();
      
      // Ottieni informazioni del deal per assegnazione e client, inclusa la data di accettazione
      const { data: dealInfo, error: dealError } = await supabase
        .from('deals')
        .select('assigned_to, title, client_id, signed_at, clients(business_name)')
        .eq('id', dealId)
        .single();

      if (dealError) throw dealError;

      const assignedTo = dealInfo?.assigned_to;
      const clientName = dealInfo?.clients?.business_name || 'Cliente';
      const dealTitle = dealInfo?.title || 'Deal';
      const signedAt = dealInfo?.signed_at;

      // Se non c'è una data di firma, usa oggi come fallback
      const baseDate = signedAt ? new Date(signedAt) : new Date();
      console.log(`🔧 Creating custom renewal tasks for service ${service.title} (${service.renewal_task_intervals.length} intervals)`);
      console.log(`📅 Base date for calculations: ${baseDate.toLocaleDateString('it-IT')} ${signedAt ? '(deal signed_at)' : '(fallback to today)'}`);

      // Per ogni intervallo configurato, crea una sales activity
      for (const interval of service.renewal_task_intervals) {
        try {
          // Calcola la data di scadenza basata sull'intervallo, partendo dalla data di accettazione del deal
          const dueDate = new Date(baseDate);
          // Usa interval.months se disponibile, altrimenti fallback a interval.days
          if (interval.months !== undefined) {
            dueDate.setMonth(dueDate.getMonth() + interval.months);
          } else if (interval.days !== undefined) {
            dueDate.setDate(dueDate.getDate() + interval.days);
          }
          const dueDateString = dueDate.toISOString();

          // Crea il titolo della task
          const taskTitle = interval.title || `Rinnovo contratto: ${service.title}`;
          
          // Crea la descrizione della task
          const taskDescription = `${interval.description || ''}\n\nServizio: ${service.title}\nCliente: ${clientName}\nDeal: ${dealTitle}\nScadenza: ${dueDate.toLocaleDateString('it-IT')}\n\n[RENEWAL_TASK:${dealServiceId}]`;

          // Determina l'operatore: usa quello specifico dell'intervallo se presente, altrimenti quello del deal
          const taskAssignedTo = interval.assigned_to || assignedTo;

          // Dati della sales activity
          const activityData = {
            organization_id: organizationId,
            deal_id: dealId,
            title: taskTitle,
            description: taskDescription,
            type: 'task',
            status: 'pending',
            assigned_to: taskAssignedTo,
            user_id: taskAssignedTo,
            due_date: dueDateString,
            metadata: {
              renewal_interval_months: interval.months,
              renewal_interval_days: interval.days,
              deal_service_id: dealServiceId,
              service_id: service.id,
              is_custom_renewal_task: true,
              base_date: baseDate.toISOString()
            }
          };

          const intervalDescription = interval.months !== undefined
            ? `${interval.months} mesi`
            : `${interval.days} giorni`;
          const assignmentInfo = interval.assigned_to
            ? `operatore specifico: ${interval.assigned_to}`
            : `operatore del deal: ${assignedTo}`;
          console.log(`📝 Creating custom renewal task: "${taskTitle}" due in ${intervalDescription} from ${baseDate.toLocaleDateString('it-IT')} (${assignmentInfo})`);

          // Inserisci la sales activity
          const { error: insertError } = await supabase
            .from('sales_activities')
            .insert(activityData);

          if (insertError) {
            console.error(`❌ Error creating custom renewal task for interval ${intervalDescription}:`, insertError);
          } else {
            console.log(`✅ Created custom renewal task: "${taskTitle}" for ${intervalDescription}, due date: ${dueDate.toLocaleDateString('it-IT')}`);
          }

        } catch (intervalError) {
          const intervalDesc = interval.months !== undefined ? `${interval.months} mesi` : `${interval.days} giorni`;
          console.error(`❌ Error processing interval ${intervalDesc}:`, intervalError);
        }
      }

      // Aggiorna il deal_service per indicare che le task personalizzate sono state create
      const { error: updateError } = await supabase
        .from('deal_services')
        .update({
          renewal_task_created: true,
          renewal_status: 'active',
          requires_manual_renewal: true // Le task personalizzate richiedono sempre intervento manuale
        })
        .eq('id', dealServiceId);

      if (updateError) {
        console.error(`❌ Error updating deal_service ${dealServiceId}:`, updateError);
      } else {
        console.log(`✅ Updated deal_service ${dealServiceId} to mark custom renewal tasks as created`);
      }

    } catch (error) {
      console.error('❌ Error creating custom renewal tasks:', error);
    }
  }

  // Metodi helper privati
  private calculateReminderDate(expiryDate: string, reminderDays: number): string {
    const expiry = new Date(expiryDate);
    expiry.setDate(expiry.getDate() - reminderDays);
    return expiry.toISOString().split('T')[0];
  }

  private calculateDaysUntilExpiry(expiryDate: string): number {
    const today = new Date();
    const expiry = new Date(expiryDate);
    const diffTime = expiry.getTime() - today.getTime();
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  }

  private calculateTaskPriority(daysUntilExpiry: number): 'low' | 'medium' | 'high' | 'urgent' {
    if (daysUntilExpiry <= 0) return 'urgent';
    if (daysUntilExpiry <= 7) return 'high';
    if (daysUntilExpiry <= 15) return 'medium';
    return 'low';
  }

  private calculateServiceExpiryDate(paymentSchedule: any, serviceType: string): string {
    const today = new Date();
    
    if (serviceType === 'annual') {
      today.setFullYear(today.getFullYear() + 1);
    } else if (serviceType === 'monthly') {
      today.setMonth(today.getMonth() + 1);
    }
    
    return today.toISOString().split('T')[0];
  }

  private async getCurrentOrganizationId(): Promise<string> {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('User not authenticated');

    const { data: profile } = await supabase
      .from('user_profiles')
      .select('organization_id')
      .eq('id', user.id)
      .single();

    if (!profile) throw new Error('User profile not found');
    return profile.organization_id;
  }

  private async createRenewalNotification(service: any, daysUntilExpiry: number): Promise<void> {
    try {
      const message = daysUntilExpiry <= 0 
        ? `URGENTE: Il contratto per ${service.services.title} del cliente ${service.deals.clients.business_name} è scaduto!`
        : `Promemoria: Il contratto per ${service.services.title} del cliente ${service.deals.clients.business_name} scadrà tra ${daysUntilExpiry} giorni.`;

      await supabase
        .from('notifications')
        .insert({
          user_id: service.deals.assigned_to,
          message,
          type: 'contract_renewal',
          priority: daysUntilExpiry <= 7 ? 'high' : 'medium',
          metadata: {
            deal_id: service.deals.id,
            deal_service_id: service.id,
            expiry_date: service.contract_expiry_date,
            days_until_expiry: daysUntilExpiry
          },
          organization_id: await this.getCurrentOrganizationId()
        });
    } catch (error) {
      console.error('Error creating renewal notification:', error);
    }
  }
}

export const contractRenewalService = new ContractRenewalService();